import { useState, useEffect, useCallback, useRef } from "react";
import { toast } from "react-toastify";
import {
  getTemplatesByMode,
  getScoresForAccreditation,
  saveTemplateScores,
  getCompletionStatus,
  submitScores as submitScoresApi,
} from "../service/templateService";

/**
 * Custom hook for managing template scoring state
 * Handles templates, scores, validation, and auto-save
 *
 * @param {Object} config - Configuration object
 * @param {string} config.accreditationId - Accreditation ID (required)
 * @param {string} config.programmeMode - Programme mode (e.g., 'Full-Time')
 * @param {string} config.templateType - Template type filter (optional)
 * @param {number} config.debounceDelay - Auto-save delay in milliseconds (default: 3000)
 * @param {boolean} config.enableAutoSave - Enable auto-save feature (default: true)
 * @returns {Object} Hook state and methods
 */
const useTemplateScoring = ({
  accreditationId,
  programmeMode,
  templateType = null,
  debounceDelay = 3000,
  enableAutoSave = true,
}) => {
  // State management
  const [templates, setTemplates] = useState([]);
  const [scores, setScores] = useState({});
  const [completionStatus, setCompletionStatus] = useState(null);
  const [isLoading, setIsLoading] = useState(true); // Start as true to prevent flash
  const [isLoadingScores, setIsLoadingScores] = useState(true); // Start as true to prevent flash
  const [isSaving, setIsSaving] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [isDirty, setIsDirty] = useState(false);
  const [lastSaved, setLastSaved] = useState(null);

  // Refs for auto-save management
  const autoSaveTimerRef = useRef(null);
  const isMountedRef = useRef(true);

  /**
   * Validate required configuration
   */
  useEffect(() => {
    if (!accreditationId) {
      console.warn("useTemplateScoring: accreditationId is not provided yet, skipping data fetch");
      // Don't set error - this is expected initially
    }
    if (!programmeMode) {
      console.warn("useTemplateScoring: programmeMode is not provided yet, skipping data fetch");
      // Don't set error - this is expected initially
    }
  }, [accreditationId, programmeMode]);

  /**
   * Fetch templates by programme mode
   */
  const fetchTemplates = useCallback(async () => {
    if (!programmeMode) {
      console.log('[useTemplateScoring] Skipping fetchTemplates - no programmeMode');
      setIsLoading(false); // Make sure loading is false
      return;
    }

    console.log('[useTemplateScoring] Fetching templates for:', { programmeMode, templateType });
    setIsLoading(true);
    setError(null);

    try {
      const templatesData = await getTemplatesByMode(programmeMode, templateType);
      console.log('[useTemplateScoring] Templates received:', templatesData);
      console.log('[useTemplateScoring] Is array?:', Array.isArray(templatesData));
      console.log('[useTemplateScoring] Length:', templatesData?.length);

      if (isMountedRef.current) {
        // Ensure we always set an array, even if empty
        setTemplates(Array.isArray(templatesData) ? templatesData : []);

        if (templatesData.length === 0) {
          console.log('[useTemplateScoring] No templates found for mode:', programmeMode);
        }
      }
    } catch (err) {
      console.error("[useTemplateScoring] Error fetching templates:", err);
      if (isMountedRef.current) {
        setError(err.message || "Failed to load templates");
        setTemplates([]); // Set empty array on error
        toast.error("Failed to load scoring templates");
      }
    } finally {
      if (isMountedRef.current) {
        console.log('[useTemplateScoring] Setting isLoading to false');
        setIsLoading(false);
      }
    }
  }, [programmeMode, templateType]);

  /**
   * Load saved scores for accreditation
   */
  const loadScores = useCallback(async () => {
    if (!accreditationId) {
      setIsLoadingScores(false);
      return;
    }

    setIsLoadingScores(true);
    setError(null);

    try {
      const scoresData = await getScoresForAccreditation(accreditationId);

      console.log('[loadScores] Raw scoresData:', scoresData);

      if (isMountedRef.current) {
        // Transform scores data into a structure organized by templateId
        const organizedScores = {};

        // Handle response structure: { scores: [...], count: ..., accreditationId: ... }
        let scoresList = [];
        if (Array.isArray(scoresData)) {
          scoresList = scoresData;
        } else if (scoresData && Array.isArray(scoresData.scores)) {
          scoresList = scoresData.scores;
        } else if (scoresData && scoresData.templateId) {
          // Single score entry
          scoresList = [scoresData];
        }

        console.log('[loadScores] Extracted scoresList:', scoresList);

        // Transform each score entry
        scoresList.forEach((scoreEntry) => {
          if (scoreEntry.templateId) {
            const templateId = scoreEntry.templateId._id || scoreEntry.templateId;

            console.log('[loadScores] Raw itemScores from backend:', scoreEntry.itemScores);

            // Transform itemScores array into a simple scores object {itemId: value}
            const scores = {};
            if (Array.isArray(scoreEntry.itemScores)) {
              scoreEntry.itemScores.forEach(item => {
                console.log('[loadScores] Processing item:', {
                  itemId: item.itemId,
                  itemName: item.itemName,
                  enteredValue: item.enteredValue,
                  isNotApplicable: item.isNotApplicable
                });
                scores[item.itemId] = item.isNotApplicable ? null : item.enteredValue;
              });
            }

            console.log('[loadScores] Transformed scores for template', templateId, ':', scores);

            organizedScores[templateId] = {
              scores: scores,
              metadata: {
                ...(scoreEntry.metadata || {}),
                totalScore: scoreEntry.totalScore,
                maxPossibleScore: scoreEntry.maxPossibleScore,
                completionPercentage: scoreEntry.completionPercentage,
                completionStatus: scoreEntry.completionStatus,
                itemsCompleted: scoreEntry.itemsCompleted,
                itemsNotApplicable: scoreEntry.itemNotApplicable,
                itemsCount: scoreEntry.itemsCount,
              },
              lastModified: scoreEntry.lastSavedAt || scoreEntry.lastModified || null,
              status: scoreEntry.status || 'draft',
            };
          }
        });

        console.log('[loadScores] Final organizedScores:', organizedScores);

        setScores(organizedScores);
        setIsDirty(false);
      }
    } catch (err) {
      console.error("Error loading scores:", err);
      if (isMountedRef.current) {
        setError(err.message || "Failed to load scores");
        // Don't show toast for 404 errors (no scores saved yet)
        if (!err.response || err.response.status !== 404) {
          toast.error("Failed to load saved scores");
        }
      }
    } finally {
      if (isMountedRef.current) {
        setIsLoadingScores(false);
      }
    }
  }, [accreditationId]);

  /**
   * Load completion status
   */
  const loadCompletionStatus = useCallback(async () => {
    if (!accreditationId) return;

    try {
      const statusData = await getCompletionStatus(accreditationId);

      if (isMountedRef.current) {
        setCompletionStatus(statusData);
      }
    } catch (err) {
      console.error("Error loading completion status:", err);
      // Don't show error toast for status loading
    }
  }, [accreditationId]);

  /**
   * Initialize hook - fetch templates, scores, and status
   */
  useEffect(() => {
    // Reset mounted ref at the start of each effect
    isMountedRef.current = true;

    console.log('[useTemplateScoring] Initialize effect:', { accreditationId, programmeMode });

    if (programmeMode) {
      console.log('[useTemplateScoring] Calling fetchTemplates');
      fetchTemplates();
    }

    if (accreditationId && programmeMode) {
      console.log('[useTemplateScoring] Calling loadScores and loadCompletionStatus');
      loadScores();
      loadCompletionStatus();
    }

    return () => {
      isMountedRef.current = false;
      if (autoSaveTimerRef.current) {
        clearTimeout(autoSaveTimerRef.current);
      }
    };
  }, [accreditationId, programmeMode, fetchTemplates, loadScores, loadCompletionStatus]);

  /**
   * Validate a single score value
   */
  const validateScore = useCallback((value, maxScore) => {
    if (value === "" || value === null || value === undefined) {
      return { valid: true, error: null }; // Empty is valid
    }

    const numValue = parseFloat(value);

    if (isNaN(numValue)) {
      return { valid: false, error: "Please enter a valid number" };
    }

    if (numValue < 0) {
      return { valid: false, error: "Score cannot be negative" };
    }

    if (maxScore && numValue > maxScore) {
      return { valid: false, error: `Score cannot exceed ${maxScore}` };
    }

    return { valid: true, error: null };
  }, []);

  /**
   * Update a single score
   */
  const updateScore = useCallback(
    (templateId, sectionId, value, maxScore = null) => {
      // Validate the score
      const validation = validateScore(value, maxScore);

      if (!validation.valid) {
        toast.error(validation.error);
        return false;
      }

      setScores((prevScores) => {
        const templateScores = prevScores[templateId] || { scores: {}, metadata: {} };

        return {
          ...prevScores,
          [templateId]: {
            ...templateScores,
            scores: {
              ...templateScores.scores,
              [sectionId]: value,
            },
            lastModified: new Date().toISOString(),
          },
        };
      });

      setIsDirty(true);
      return true;
    },
    [validateScore]
  );

  /**
   * Update multiple scores at once
   */
  const updateScores = useCallback((templateId, newScores) => {
    setScores((prevScores) => {
      const templateScores = prevScores[templateId] || { scores: {}, metadata: {} };

      return {
        ...prevScores,
        [templateId]: {
          ...templateScores,
          scores: {
            ...templateScores.scores,
            ...newScores,
          },
          lastModified: new Date().toISOString(),
        },
      };
    });

    setIsDirty(true);
  }, []);

  /**
   * Save scores to backend
   */
  const saveScores = useCallback(
    async (templateId = null, showToast = true) => {
      if (!accreditationId) {
        toast.error("Missing accreditation ID");
        return false;
      }

      setIsSaving(true);
      setError(null);

      try {
        // If templateId provided, save only that template
        // Otherwise, save all templates with scores
        const templatesToSave = templateId
          ? [templateId]
          : Object.keys(scores).filter(id => Object.keys(scores[id]?.scores || {}).length > 0);

        if (templatesToSave.length === 0) {
          if (showToast) {
            toast.info("No scores to save");
          }
          setIsSaving(false);
          return true;
        }

        // Save each template's scores
        const savePromises = templatesToSave.map((tid) => {
          const templateData = scores[tid];
          return saveTemplateScores({
            accreditationId,
            templateId: tid,
            scores: templateData.scores || {},
            metadata: {
              ...templateData.metadata,
              lastModified: new Date().toISOString(),
            },
          });
        });

        await Promise.all(savePromises);

        if (isMountedRef.current) {
          setIsDirty(false);
          setLastSaved(new Date().toISOString());

          if (showToast) {
            toast.success("Scores saved successfully");
          }

          // Refresh completion status
          await loadCompletionStatus();
        }

        return true;
      } catch (err) {
        console.error("Error saving scores:", err);
        if (isMountedRef.current) {
          setError(err.message || "Failed to save scores");
          if (showToast) {
            toast.error("Failed to save scores");
          }
        }
        return false;
      } finally {
        if (isMountedRef.current) {
          setIsSaving(false);
        }
      }
    },
    [accreditationId, scores, loadCompletionStatus]
  );

  /**
   * Auto-save effect with debouncing
   */
  useEffect(() => {
    if (isDirty && enableAutoSave && accreditationId) {
      // Clear existing timer
      if (autoSaveTimerRef.current) {
        clearTimeout(autoSaveTimerRef.current);
      }

      // Set new timer
      autoSaveTimerRef.current = setTimeout(() => {
        saveScores(null, false); // Auto-save without toast
      }, debounceDelay);
    }

    // Cleanup
    return () => {
      if (autoSaveTimerRef.current) {
        clearTimeout(autoSaveTimerRef.current);
      }
    };
  }, [scores, isDirty, enableAutoSave, accreditationId, saveScores, debounceDelay]);

  /**
   * Get template status (completion %)
   */
  const getTemplateStatus = useCallback(
    (templateId) => {
      const template = templates.find((t) => t._id === templateId);
      const templateScores = scores[templateId]?.scores || {};

      if (!template || !template.sections) {
        return {
          completed: 0,
          total: 0,
          percentage: 0,
          isComplete: false,
          totalScore: 0,
          maxScore: 0,
        };
      }

      let completed = 0;
      let total = 0;
      let totalScore = 0;
      let maxScore = 0;

      template.sections.forEach((section) => {
        total++;
        maxScore += section.maxScore || 0;

        const sectionScore = templateScores[section._id];
        if (sectionScore !== null && sectionScore !== undefined && sectionScore !== "") {
          completed++;
          totalScore += parseFloat(sectionScore) || 0;
        }
      });

      return {
        completed,
        total,
        percentage: total > 0 ? Math.round((completed / total) * 100) : 0,
        isComplete: completed === total && total > 0,
        totalScore: parseFloat(totalScore.toFixed(2)),
        maxScore,
      };
    },
    [templates, scores]
  );

  /**
   * Get overall completion status
   */
  const getOverallStatus = useCallback(() => {
    if (templates.length === 0) {
      return {
        completed: 0,
        total: 0,
        percentage: 0,
        isComplete: false,
      };
    }

    let completedTemplates = 0;
    const totalTemplates = templates.length;

    templates.forEach((template) => {
      const scoreData = scores[template._id];

      // Check if template is complete using backend completionStatus or by checking all items
      if (scoreData?.metadata?.completionStatus === 'complete') {
        // Use backend calculation if available
        completedTemplates++;
      } else if (scoreData?.scores) {
        // Fallback: check if all items are scored or marked as N/A
        const items = template.items || [];
        const totalItems = items.length;
        let handledItems = 0;

        items.forEach((item) => {
          const itemScore = scoreData.scores[item._id] || scoreData.scores[item.name];
          const isNa = itemScore === null;
          const hasScore = itemScore !== undefined && itemScore !== null && itemScore !== "";

          if (isNa || hasScore) {
            handledItems++;
          }
        });

        if (handledItems === totalItems && totalItems > 0) {
          completedTemplates++;
        }
      }
    });

    return {
      completed: completedTemplates,
      total: totalTemplates,
      percentage: totalTemplates > 0 ? Math.round((completedTemplates / totalTemplates) * 100) : 0,
      isComplete: completedTemplates === totalTemplates && totalTemplates > 0,
    };
  }, [templates, scores]);

  /**
   * Submit all scores for review
   */
  const submitAllScores = useCallback(async () => {
    if (!accreditationId) {
      toast.error("Missing accreditation ID");
      return false;
    }

    // Check if all templates are complete
    const overallStatus = getOverallStatus();
    if (!overallStatus.isComplete) {
      toast.warning(`Please complete all templates before submitting (${overallStatus.completed}/${overallStatus.total} completed)`);
      return false;
    }

    setIsSubmitting(true);
    setError(null);

    try {
      // First, save any unsaved changes
      if (isDirty) {
        const saved = await saveScores(null, false);
        if (!saved) {
          throw new Error("Failed to save pending changes");
        }
      }

      // Submit scores
      await submitScoresApi(accreditationId);

      if (isMountedRef.current) {
        toast.success("Scores submitted successfully for review");
        await loadCompletionStatus();
      }

      return true;
    } catch (err) {
      console.error("Error submitting scores:", err);
      if (isMountedRef.current) {
        setError(err.message || "Failed to submit scores");
        toast.error("Failed to submit scores");
      }
      return false;
    } finally {
      if (isMountedRef.current) {
        setIsSubmitting(false);
      }
    }
  }, [accreditationId, getOverallStatus, isDirty, saveScores, loadCompletionStatus]);

  /**
   * Reset scores for a template
   */
  const resetTemplate = useCallback((templateId) => {
    setScores((prevScores) => {
      const newScores = { ...prevScores };
      delete newScores[templateId];
      return newScores;
    });
    setIsDirty(true);
    toast.info("Template scores cleared");
  }, []);

  /**
   * Get scores for a specific template
   */
  const getTemplateScores = useCallback(
    (templateId) => {
      return scores[templateId]?.scores || {};
    },
    [scores]
  );

  return {
    // Data
    templates,
    scores,
    completionStatus,
    lastSaved,

    // State
    isLoading,
    isLoadingScores,
    isSaving,
    isSubmitting,
    error,
    isDirty,

    // Methods
    updateScore,
    updateScores,
    saveScores,
    submitAllScores,
    getTemplateStatus,
    getOverallStatus,
    getTemplateScores,
    resetTemplate,
    refreshTemplates: fetchTemplates,
    refreshScores: loadScores,
    refreshStatus: loadCompletionStatus,
  };
};

export default useTemplateScoring;
